import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, Check, ZoomIn } from "lucide-react";
import { toast } from "sonner";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", () => reject(new Error("Immagine non valida")));
    img.src = url;
  });

async function getCroppedBlob(imageSrc, cropPixels, size = 512) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    size,
    size
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}

const SquareCropModal = ({ imageSrc, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixels);
      await onConfirm(blob);
    } catch (err) {
      console.error("crop failed:", err);
      toast.error("Impossibile ritagliare questa immagine");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4" data-testid="logo-crop-modal">
      <div className="bg-white rounded-3xl overflow-hidden w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <p className="text-sm font-black text-zinc-900">Ritaglia il logo (quadrato)</p>
          <button onClick={onCancel} className="w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors" data-testid="logo-crop-cancel" aria-label="Annulla">
            <X className="w-4 h-4 text-zinc-700" />
          </button>
        </div>
        <div className="relative w-full h-72 bg-zinc-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-3">
            <ZoomIn className="w-4 h-4 text-zinc-400 shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-zinc-900"
              data-testid="logo-crop-zoom"
            />
          </div>
          <button
            onClick={confirm}
            disabled={saving}
            className="w-full py-3 rounded-2xl bg-zinc-900 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="logo-crop-confirm"
          >
            <Check className="w-4 h-4" /> {saving ? "Salvataggio…" : "Applica ritaglio"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SquareCropModal;
